import { faImage } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import { useEffect, useState, type CSSProperties } from 'react'
import { MoonLoader } from 'react-spinners'
import { trpc } from '~/Resources/TRPC'

interface ImageLibraryImagePreviewProps {
	imageName: string
	type: 'original' | 'preview'
	checksum: string
	className?: string
	alt?: string
	onLoad?: (naturalWidth: number, naturalHeight: number) => void
	onError?: (error: string) => void
}

// interface LoadState {
// 	loading: boolean
// 	imageUrl: string | null
// 	error: string | null
// }

export function ImageLibraryImagePreview({
	imageName,
	type,
	checksum,
	className,
	alt,
	onLoad,
}: ImageLibraryImagePreviewProps): React.JSX.Element {
	const {
		data: queryData,
		isLoading: queryLoading,
		error: queryError,
		refetch: queryRefetch,
	} = useQuery(
		trpc.imageLibrary.getData.queryOptions({
			imageName,
			type,
		})
	)

	const [aspect, setAspect] = useState<number | null>(null)

	useEffect(() => {
		setAspect(null)
		queryRefetch().catch((err) => {
			console.error('Failed to refetch image data:', err)
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [imageName, type, checksum])

	const checksumMatches = !queryData || queryData.checksum === checksum

	if (queryLoading || !checksumMatches) {
		return (
			<div className={classNames('image-library-preview-loading', className)}>
				<MoonLoader />
			</div>
		)
	}

	if (queryError) {
		return (
			<div className={classNames('image-library-preview-error', className)}>
				<span>{queryError.message}</span>
			</div>
		)
	}

	if (!queryData?.image) {
		return (
			<div className={classNames('image-library-preview-empty', className)} title="No image data">
				<FontAwesomeIcon icon={faImage} />
			</div>
		)
	}

	return (
		<img
			src={queryData.image}
			alt={alt || 'Image preview'}
			className={classNames('image-library-preview', className)}
			style={aspect != null ? ({ '--img-aspect': aspect } as CSSProperties) : undefined}
			onLoad={(e) => {
				const { naturalWidth: w, naturalHeight: h } = e.currentTarget
				if (w && h) setAspect(w / h)
				onLoad?.(w, h)
			}}
		/>
	)
}
